import axios from "axios";
import { HEADERS } from "../UrlAndPaths";
import { errorSnackMsg, successSnackMsg } from "../../component/Custom Hooks/SnackkBarUtilities";

/**
 * Check if device needs verification
 * @param {Object} body - { userName, companyCode, deviceId, prefix }
 * @returns {Promise}
 */
export const checkDeviceAPI = async (body) => {
  try {
    const res = await axios.post("admin/checkdevice", body, HEADERS);
    return res.data;
  } catch (err) {
    console.error("Check device error:", err);
    throw err;
  }
};

/**
 * Request OTP for device verification
 * @param {Object} body - { userName, companyCode, deviceId, prefix }
 * @returns {Promise}
 */
export const requestDeviceOTPAPI = async (body) => {
  try {
    const res = await axios.post("admin/requestdeviceotp", body, HEADERS);
    if (res.status === 200) {
      successSnackMsg(res.data.message || "OTP sent successfully");
    }
    return res.data;
  } catch (err) {
    console.error("Request OTP error:", err);
    errorSnackMsg(err.response?.data?.message || "Failed to send OTP");
    throw err;
  }
};

/**
 * Verify OTP and add device to trusted list
 * @param {Object} body - { userName, companyCode, deviceId, otp, prefix, deviceName }
 * @returns {Promise}
 */
export const verifyDeviceOTPAPI = async (body) => {
  try {
    const res = await axios.post("admin/verifydeviceotp", body, HEADERS);
    if (res.status === 200) {
      successSnackMsg(res.data.message || "Device verified successfully");
    }
    return res.data;
  } catch (err) {
    console.error("Verify OTP error:", err);
    errorSnackMsg(err.response?.data?.message || "Invalid or expired OTP");
    throw err;
  }
};

/**
 * Update registered email for admin
 * @param {Object} body - { email }
 * @returns {Promise}
 */
export const updateRegisteredEmailAPI = async (body) => {
  try {
    const res = await axios.post("admin/updateregisteredemail", body, HEADERS);
    if (res.status === 200) {
      successSnackMsg(res.data.message || "Email updated successfully");
    }
    return res.data;
  } catch (err) {
    console.error("Update email error:", err);
    errorSnackMsg(err.response?.data?.message || "Failed to update email");
    throw err;
  }
};

/**
 * Get list of trusted devices
 * @returns {Promise}
 */
export const getTrustedDevicesAPI = async () => {
  try {
    const res = await axios.get("admin/trusteddevices", HEADERS);
    return res.data;
  } catch (err) {
    console.error("Get devices error:", err);
    errorSnackMsg("Failed to fetch trusted devices");
    throw err;
  }
};

/**
 * Remove a trusted device
 * @param {Object} body - { deviceId }
 * @returns {Promise}
 */
export const removeTrustedDeviceAPI = async (body) => {
  try {
    const res = await axios.post("admin/removedevice", body, HEADERS);
    if (res.status === 200) {
      successSnackMsg(res.data.message || "Device removed successfully");
    }
    return res.data;
  } catch (err) {
    console.error("Remove device error:", err);
    errorSnackMsg("Failed to remove device");
    throw err;
  }
};

/**
 * Request OTP for email change (sent to current email)
 * @param {Object} body - { newEmail }
 * @returns {Promise}
 */
export const requestEmailChangeOTPAPI = async (body) => {
  try {
    const res = await axios.post("admin/requestemailchangeotp", body, HEADERS);
    if (res.status === 200) {
      successSnackMsg(res.data.message || "OTP sent to your current email");
    }
    return res.data;
  } catch (err) {
    console.error("Request email change OTP error:", err);
    errorSnackMsg(err.response?.data?.message || "Failed to send OTP");
    throw err;
  }
};

/**
 * Verify OTP and update email
 * @param {Object} body - { newEmail, otp }
 * @returns {Promise}
 */
export const verifyEmailChangeOTPAPI = async (body) => {
  try {
    const res = await axios.post("admin/verifyemailchangeotp", body, HEADERS);
    if (res.status === 200) {
      successSnackMsg(res.data.message || "Email updated successfully");
    }
    return res.data;
  } catch (err) {
    console.error("Verify email change OTP error:", err);
    errorSnackMsg(err.response?.data?.message || "Invalid or expired OTP");
    throw err;
  }
};

/**
 * Get all employees for device assignment
 * @returns {Promise}
 */
export const getEmployeesForDeviceAPI = async () => {
  try {
    const res = await axios.get("admin/employeesfordevice", HEADERS);
    return res.data;
  } catch (err) {
    console.error("Get employees error:", err);
    errorSnackMsg("Failed to load employees");
    throw err;
  }
};

/**
 * Get device assignments
 * @returns {Promise}
 */
export const getDeviceAssignmentsAPI = async () => {
  try {
    const res = await axios.get("admin/deviceassignments", HEADERS);
    return res.data;
  } catch (err) {
    console.error("Get device assignments error:", err);
    errorSnackMsg("Failed to load device assignments");
    throw err;
  }
};

/**
 * Assign employees to device
 * @param {Object} body - { deviceId, employeeIds: [] }
 * @returns {Promise}
 */
export const assignEmployeesToDeviceAPI = async (body) => {
  try {
    const res = await axios.post("admin/assignemployeestodevice", body, HEADERS);
    if (res.status === 200) {
      successSnackMsg(res.data.message || "Employees assigned successfully");
    }
    return res.data;
  } catch (err) {
    console.error("Assign employees error:", err);
    errorSnackMsg(err.response?.data?.message || "Failed to assign employees");
    throw err;
  }
};
